import { Fragment, useState } from "react";
import { LinkSimple, TextB, TextItalic, ListBullets } from "@phosphor-icons/react";

// [label](https://url) | bare URL | **bold** | *italic*
const INLINE_RE = new RegExp(
  String.raw`\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)` +
  String.raw`|((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]'"])` +
  String.raw`|\*\*(\S(?:.*?\S)?)\*\*` +
  String.raw`|(?:^|(?<=[^*\w]))\*(\S(?:[^*\n]*?\S)?)\*(?![*\w])`, "g");
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;

function Inline({ text }) {
  const out = [];
  let last = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[4]) out.push(<strong key={m.index}>{m[4]}</strong>);
    else if (m[5]) out.push(<em key={m.index}>{m[5]}</em>);
    else {
      const href = m[2] || (m[3].startsWith("www.") ? `https://${m[3]}` : m[3]);
      out.push(
        <a key={m.index} href={href} target="_blank" rel="noopener noreferrer"
          className="underline underline-offset-2 break-all" style={{ color: "var(--text)" }}
          onClick={(e) => e.stopPropagation()}>{m[1] || m[3]}</a>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>;
}

// Renders message text with links, **bold**, *italic* and "- " bullet lists. Use inside whitespace-pre-line.
export function LinkifiedText({ text }) {
  const blocks = [];
  let list = null;
  (text || "").split("\n").forEach((line, i, arr) => {
    const m = line.match(BULLET_RE);
    if (m) {
      if (!list) { list = []; blocks.push({ list }); }
      list.push(m[1]);
      return;
    }
    list = null;
    blocks.push({ line, br: i < arr.length - 1 });
  });
  return (
    <>
      {blocks.map((b, i) => b.list ? (
        <ul key={i} className="list-disc pl-5 my-1 space-y-0.5 whitespace-normal">
          {b.list.map((li, j) => <li key={j}><Inline text={li} /></li>)}
        </ul>
      ) : (
        <Fragment key={i}><Inline text={b.line} />{b.br && "\n"}</Fragment>
      ))}
    </>
  );
}

export function InsertLinkButton({ value, onChange, testid = "insert-link" }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const insert = () => {
    let u = url.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    const piece = label.trim() ? `[${label.trim()}](${u})` : u;
    onChange(`${value || ""}${value && !/\s$/.test(value) ? " " : ""}${piece}`);
    setOpen(false); setLabel(""); setUrl("");
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={testid}
        className="btn-ghost text-xs h-8" title="Insert a hyperlink">
        <LinkSimple size={11} weight="bold" /> Insert link
      </button>
    );
  }
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); insert(); }
    if (e.key === "Escape") setOpen(false);
  };
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`${testid}-form`}>
      <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={onKey}
        placeholder="Link text (optional)" data-testid={`${testid}-label`} className="px-3 py-2 text-xs w-40" />
      <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={onKey}
        placeholder="https://…" data-testid={`${testid}-url`} className="px-3 py-2 text-xs w-48" />
      <button type="button" onClick={insert} disabled={!url.trim()} data-testid={`${testid}-add`}
        className="btn-primary text-xs h-8">Add</button>
      <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs h-8">Cancel</button>
    </div>
  );
}

// Bold / italic / bullet list / link toolbar for a textarea found by its data-testid.
export function FormatBar({ value, onChange, target, testid = "fmt" }) {
  const apply = (kind) => {
    const el = document.querySelector(`[data-testid="${target}"]`);
    const v = value || "";
    const start = el ? el.selectionStart : v.length;
    const end = el ? el.selectionEnd : v.length;
    const sel = v.slice(start, end);
    let next, selFrom, selTo;
    if (kind === "list") {
      const ls = v.lastIndexOf("\n", start - 1) + 1;
      const block = v.slice(ls, end) || "";
      const lines = (block || "List item").split("\n").map(l => (BULLET_RE.test(l) ? l : `- ${l}`));
      const joined = lines.join("\n");
      next = v.slice(0, ls) + joined + v.slice(end);
      selFrom = ls; selTo = ls + joined.length;
    } else {
      const mark = kind === "bold" ? "**" : "*";
      const inner = sel || (kind === "bold" ? "bold text" : "italic text");
      next = v.slice(0, start) + mark + inner + mark + v.slice(end);
      selFrom = start + mark.length; selTo = selFrom + inner.length;
    }
    onChange(next);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(selFrom, selTo); } });
  };
  const btn = "btn-ghost text-xs h-8 justify-center !px-2.5";
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      <button type="button" onClick={() => apply("bold")} className={btn} title="Bold (**text**)"
        aria-label="Bold" data-testid={`${testid}-bold`}><TextB size={13} weight="bold" /></button>
      <button type="button" onClick={() => apply("italic")} className={btn} title="Italic (*text*)"
        aria-label="Italic" data-testid={`${testid}-italic`}><TextItalic size={13} weight="bold" /></button>
      <button type="button" onClick={() => apply("list")} className={btn} title="Bullet list (- item)"
        aria-label="Bullet list" data-testid={`${testid}-list`}><ListBullets size={13} weight="bold" /></button>
      <InsertLinkButton value={value} onChange={onChange} testid={`${testid}-link`} />
    </div>
  );
}
